import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, Eye, CheckCircle, Monitor, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  PLAYING: 'playing',
  FINISHED: 'finished',
};

// Device Mode Selection Component
const DeviceModeSelector = ({ onSelect }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you're taking this test for the best experience
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Larger display</p>
          </div>
        </Button>
        
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Mobile/Tablet</p>
            <p className="text-xs text-[#64748B]">Touch controls</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Score Display Component
const ScoreDisplay = ({ score }) => (
  <div className="text-center">
    <p className="text-xs text-[#64748B] uppercase tracking-wide">Score</p>
    <p className="font-mono text-xl sm:text-2xl font-bold text-[#0B1D51]">{score}</p>
  </div>
);

// Railway and traffic scenes organized by level - each level has unique verified images
// All images are rail/traffic related with accurate questions matching image content
const LEVEL_SCENES = {
  1: [ // Level 1 - Basic railway scenes, longer display time (1.5s)
    {
      id: 1,
      imageUrl: "https://images.unsplash.com/photo-1744381326084-b1bb61996a04?w=800&q=80",
      displayTime: 1500,
      question: "What did you observe in this railway scene?",
      answers: [
        { text: "People waiting on platform", correct: true },
        { text: "Empty platform", correct: false },
        { text: "Multiple parallel tracks visible", correct: true },
        { text: "Single track only", correct: false },
      ]
    },
    {
      id: 2,
      imageUrl: "https://images.unsplash.com/photo-1736132920914-a40f90d09d8c?w=800&q=80",
      displayTime: 1500,
      question: "What was visible at this station?",
      answers: [
        { text: "Train on the tracks", correct: true },
        { text: "Empty tracks only", correct: false },
        { text: "Station platform visible", correct: true },
        { text: "Tunnel entrance", correct: false },
      ]
    },
    {
      id: 3,
      imageUrl: "https://images.unsplash.com/photo-1693572322416-9ea4b36482ce?w=800&q=80",
      displayTime: 1500,
      question: "What type of scene was shown?",
      answers: [
        { text: "Train parked at platform", correct: true },
        { text: "Moving train", correct: false },
        { text: "Covered station area", correct: true },
        { text: "Open air setting only", correct: false },
      ]
    },
    {
      id: 4,
      imageUrl: "https://images.unsplash.com/photo-1692342380430-6fa4010758bb?w=800&q=80",
      displayTime: 1500,
      question: "What did you see in this railway scene?",
      answers: [
        { text: "Train station with tracks", correct: true },
        { text: "Railway bridge", correct: false },
        { text: "Platform area visible", correct: true },
        { text: "Tunnel only", correct: false },
      ]
    },
    {
      id: 5,
      imageUrl: "https://images.unsplash.com/photo-1584830257803-1d2602fe6b86?w=800&q=80",
      displayTime: 1500,
      question: "What cab view elements were visible?",
      answers: [
        { text: "Train driver visible from behind", correct: true },
        { text: "Empty cab", correct: false },
        { text: "Green train exterior visible", correct: true },
        { text: "Red train exterior", correct: false },
      ]
    },
    {
      id: 101,
      imageUrl: "https://images.unsplash.com/photo-1752312304247-b920759fc551?w=800&q=80",
      displayTime: 1500,
      question: "What control panel features were visible?",
      answers: [
        { text: "Train control dashboard", correct: true },
        { text: "Passenger seating area", correct: false },
        { text: "Multiple screens/monitors", correct: true },
        { text: "No instruments visible", correct: false },
      ]
    },
    {
      id: 102,
      imageUrl: "https://images.unsplash.com/photo-1750933177281-ae99f1f38311?w=800&q=80",
      displayTime: 1500,
      question: "What did you notice in this cab view?",
      answers: [
        { text: "View through front windscreen", correct: true },
        { text: "Side window view only", correct: false },
        { text: "Cab interior visible", correct: true },
        { text: "Outdoor platform view", correct: false },
      ]
    },
    {
      id: 103,
      imageUrl: "https://images.unsplash.com/photo-1733593202034-e069d982209d?w=800&q=80",
      displayTime: 1500,
      question: "What track view was shown?",
      answers: [
        { text: "View from inside cab looking out", correct: true },
        { text: "External train view", correct: false },
        { text: "Field/countryside visible", correct: true },
        { text: "Urban buildings only", correct: false },
      ]
    },
  ],
  2: [ // Level 2 - Station and signal scenes (1.3s display)
    {
      id: 6,
      imageUrl: "https://images.unsplash.com/photo-1610611342266-bcc131ecc78c?w=800&q=80",
      displayTime: 1300,
      question: "What did you observe in this track scene?",
      answers: [
        { text: "Multiple parallel tracks visible", correct: true },
        { text: "Single track only", correct: false },
        { text: "Tracks stretching into distance", correct: true },
        { text: "Platform beside tracks", correct: false },
        { text: "Railway infrastructure overhead", correct: true },
      ]
    },
    {
      id: 7,
      imageUrl: "https://images.unsplash.com/photo-1622504789958-449b1f7a12c0?w=800&q=80",
      displayTime: 1300,
      question: "What was visible in this railway scene?",
      answers: [
        { text: "Railway tracks through grass field", correct: true },
        { text: "Urban station setting", correct: false },
        { text: "Daytime clear conditions", correct: true },
        { text: "Night time scene", correct: false },
        { text: "Rural landscape", correct: true },
      ]
    },
    {
      id: 8,
      imageUrl: "https://images.unsplash.com/photo-1612083111232-29f08821e47a?w=800&q=80",
      displayTime: 1300,
      question: "What track features were shown?",
      answers: [
        { text: "Railway tracks during daytime", correct: true },
        { text: "Night scene", correct: false },
        { text: "Overhead electric lines visible", correct: true },
        { text: "No overhead equipment", correct: false },
        { text: "Clear weather conditions", correct: true },
      ]
    },
    {
      id: 9,
      imageUrl: "https://images.unsplash.com/photo-1601082488718-bbe45de05517?w=800&q=80",
      displayTime: 1300,
      question: "What railway junction elements were visible?",
      answers: [
        { text: "Multiple track junction/points", correct: true },
        { text: "Single straight track", correct: false },
        { text: "Track switching equipment", correct: true },
        { text: "No switches visible", correct: false },
        { text: "Ballast/gravel trackbed", correct: true },
      ]
    },
    {
      id: 10,
      imageUrl: "https://images.unsplash.com/photo-1766640027652-86a69d906612?w=800&q=80",
      displayTime: 1300,
      question: "What level crossing features were visible?",
      answers: [
        { text: "Railroad crossing with gates", correct: true },
        { text: "Open crossing only", correct: false },
        { text: "Warning lights visible", correct: true },
        { text: "No warning equipment", correct: false },
        { text: "Ocean/water view beyond", correct: true },
      ]
    },
    {
      id: 201,
      imageUrl: "https://images.unsplash.com/photo-1588362951121-3ee319b018b2?w=800&q=80",
      displayTime: 1300,
      question: "What crossing scene was shown?",
      answers: [
        { text: "Level crossing barrier visible", correct: true },
        { text: "No barriers present", correct: false },
        { text: "Vehicle at crossing", correct: true },
        { text: "No vehicles visible", correct: false },
        { text: "Road crossing railway", correct: true },
      ]
    },
    {
      id: 202,
      imageUrl: "https://images.unsplash.com/photo-1739964552378-0fb72f3e3291?w=800&q=80",
      displayTime: 1300,
      question: "What crossing signage was visible?",
      answers: [
        { text: "Red and white cross sign", correct: true },
        { text: "Green light signal", correct: false },
        { text: "Railway warning sign", correct: true },
        { text: "No warning signs", correct: false },
        { text: "Clear sky visible", correct: true },
      ]
    },
    {
      id: 203,
      imageUrl: "https://images.unsplash.com/photo-1658874919865-35bb32505edc?w=800&q=80",
      displayTime: 1300,
      question: "What railway yard features did you notice?",
      answers: [
        { text: "Multiple freight trains", correct: true },
        { text: "Passenger trains only", correct: false },
        { text: "Railway yard with many tracks", correct: true },
        { text: "Single track depot", correct: false },
        { text: "Industrial railway setting", correct: true },
      ]
    },
  ],
  3: [ // Level 3 - Level crossings and signals (1.1s display)
    {
      id: 11,
      imageUrl: "https://images.unsplash.com/photo-1771150536355-b4de2cec8b11?w=800&q=80",
      displayTime: 1100,
      question: "What freight yard features were visible?",
      answers: [
        { text: "Freight trains parked in yard", correct: true },
        { text: "Passenger trains only", correct: false },
        { text: "Night time scene", correct: true },
        { text: "Daytime scene", correct: false },
        { text: "Multiple parallel tracks", correct: true },
        { text: "Single track only", correct: false },
      ]
    },
    {
      id: 12,
      imageUrl: "https://images.unsplash.com/photo-1771150535337-debe34189e58?w=800&q=80",
      displayTime: 1100,
      question: "What railway yard scene did you observe?",
      answers: [
        { text: "Freight train cars at railyard", correct: true },
        { text: "Empty railway yard", correct: false },
        { text: "Night time lighting", correct: true },
        { text: "Natural daylight", correct: false },
        { text: "Industrial railway setting", correct: true },
        { text: "Rural station setting", correct: false },
      ]
    },
    {
      id: 13,
      imageUrl: "https://images.unsplash.com/photo-1722518015402-bf99debf169f?w=800&q=80",
      displayTime: 1100,
      question: "What yard features were shown?",
      answers: [
        { text: "Multiple train cars in yard", correct: true },
        { text: "Single locomotive only", correct: false },
        { text: "Railway infrastructure visible", correct: true },
        { text: "No infrastructure", correct: false },
        { text: "Industrial freight area", correct: true },
        { text: "Passenger terminal", correct: false },
      ]
    },
    {
      id: 14,
      imageUrl: "https://images.unsplash.com/photo-1618684004782-d7532f894f78?w=800&q=80",
      displayTime: 1100,
      question: "What locomotive interior elements were visible?",
      answers: [
        { text: "Industrial train machinery", correct: true },
        { text: "Passenger seating", correct: false },
        { text: "Control mechanisms visible", correct: true },
        { text: "No controls present", correct: false },
        { text: "Dark interior environment", correct: true },
        { text: "Bright outdoor scene", correct: false },
      ]
    },
    {
      id: 15,
      imageUrl: "https://images.unsplash.com/photo-1683120012759-25f779bcac64?w=800&q=80",
      displayTime: 1100,
      question: "What cab interior was shown?",
      answers: [
        { text: "Train/vehicle interior view", correct: true },
        { text: "External train view", correct: false },
        { text: "Control equipment visible", correct: true },
        { text: "Empty compartment", correct: false },
        { text: "Green coloured elements", correct: true },
        { text: "Red coloured interior", correct: false },
      ]
    },
    {
      id: 301,
      imageUrl: "https://images.unsplash.com/photo-1521668824193-5f71bd60c5b8?w=800&q=80",
      displayTime: 1100,
      question: "What control panel was visible?",
      answers: [
        { text: "Electronic control device", correct: true },
        { text: "Manual controls only", correct: false },
        { text: "Black and grey colours", correct: true },
        { text: "Bright colourful display", correct: false },
        { text: "Dashboard equipment", correct: true },
        { text: "No instruments visible", correct: false },
      ]
    },
    {
      id: 302,
      imageUrl: "https://images.unsplash.com/photo-1612525736240-3cb22fd26a32?w=800&q=80",
      displayTime: 1100,
      question: "What control mechanism was shown?",
      answers: [
        { text: "Gear shift/lever control", correct: true },
        { text: "Steering wheel", correct: false },
        { text: "Silver/metallic component", correct: true },
        { text: "Wooden control", correct: false },
        { text: "Vehicle interior setting", correct: true },
        { text: "Outdoor scene", correct: false },
      ]
    },
    {
      id: 303,
      imageUrl: "https://images.unsplash.com/photo-1654516614321-0e7b35aee4e7?w=800&q=80",
      displayTime: 1100,
      question: "What railway signage was present?",
      answers: [
        { text: "Railway warning sign", correct: true },
        { text: "Road speed sign", correct: false },
        { text: "Building in background", correct: true },
        { text: "Open field only", correct: false },
        { text: "Close-up view of sign", correct: true },
        { text: "Wide landscape view", correct: false },
      ]
    },
  ],
  4: [ // Level 4 - Cab views and train interiors (0.9s display)
    {
      id: 16,
      imageUrl: "https://images.unsplash.com/photo-1540804236945-b6ae24bfe0a9?w=800&q=80",
      displayTime: 900,
      question: "What landscape elements were visible?",
      answers: [
        { text: "Railway tracks through forest", correct: true },
        { text: "Urban station setting", correct: false },
        { text: "Tall trees on both sides", correct: true },
        { text: "Open desert landscape", correct: false },
        { text: "Autumn/fall colours visible", correct: true },
        { text: "Summer green only", correct: false },
      ]
    },
    {
      id: 17,
      imageUrl: "https://images.unsplash.com/photo-1588127727253-e5f2faf4f541?w=800&q=80",
      displayTime: 900,
      question: "What did you see in this track scene?",
      answers: [
        { text: "Railway tracks under blue sky", correct: true },
        { text: "Underground tunnel", correct: false },
        { text: "Clear daytime conditions", correct: true },
        { text: "Night time scene", correct: false },
        { text: "Open landscape setting", correct: true },
        { text: "Station platform setting", correct: false },
      ]
    },
    {
      id: 18,
      imageUrl: "https://images.unsplash.com/photo-1501704163333-86d3832cd4ea?w=800&q=80",
      displayTime: 900,
      question: "What scenic railway features were shown?",
      answers: [
        { text: "Railroad surrounded by trees", correct: true },
        { text: "Industrial railway area", correct: false },
        { text: "Daytime natural lighting", correct: true },
        { text: "Artificial station lighting", correct: false },
        { text: "Perspective view down tracks", correct: true },
        { text: "Side view of trains", correct: false },
      ]
    },
    {
      id: 19,
      imageUrl: "https://images.unsplash.com/photo-1556695736-d287caebc48e?w=800&q=80",
      displayTime: 900,
      question: "What station scene was visible?",
      answers: [
        { text: "Person near train", correct: true },
        { text: "Empty platform", correct: false },
        { text: "Underground station setting", correct: true },
        { text: "Outdoor platform", correct: false },
        { text: "Train doors visible", correct: true },
        { text: "Only tracks visible", correct: false },
      ]
    },
    {
      id: 20,
      imageUrl: "https://images.unsplash.com/photo-1529179307417-ca83d48bd790?w=800&q=80",
      displayTime: 900,
      question: "What did you observe in this station?",
      answers: [
        { text: "Underground train station", correct: true },
        { text: "Outdoor platform", correct: false },
        { text: "Train at platform", correct: true },
        { text: "Empty tracks", correct: false },
        { text: "Curved platform design", correct: true },
        { text: "Straight platform only", correct: false },
      ]
    },
    {
      id: 401,
      imageUrl: "https://images.unsplash.com/photo-1556624651-1f527cdf6508?w=800&q=80",
      displayTime: 900,
      question: "What metro station features were visible?",
      answers: [
        { text: "Train passing at speed", correct: true },
        { text: "Stationary train", correct: false },
        { text: "Motion blur effect", correct: true },
        { text: "Sharp stationary image", correct: false },
        { text: "Blue and grey colours", correct: true },
        { text: "Red and white colours", correct: false },
      ]
    },
    {
      id: 402,
      imageUrl: "https://images.unsplash.com/photo-1521075821520-ae40128c1ea0?w=800&q=80",
      displayTime: 900,
      question: "What subway scene was shown?",
      answers: [
        { text: "Moving train/time lapse", correct: true },
        { text: "Stationary train", correct: false },
        { text: "Underground platform", correct: true },
        { text: "Open air station", correct: false },
        { text: "Blue support columns", correct: true },
        { text: "No columns visible", correct: false },
      ]
    },
    {
      id: 403,
      imageUrl: "https://images.unsplash.com/photo-1618684004782-d7532f894f78?w=800&q=80",
      displayTime: 900,
      question: "What industrial train features were visible?",
      answers: [
        { text: "Black and red machinery", correct: true },
        { text: "White clean equipment", correct: false },
        { text: "Industrial controls", correct: true },
        { text: "Passenger seating", correct: false },
        { text: "Dark interior environment", correct: true },
        { text: "Bright outdoor scene", correct: false },
      ]
    },
  ],
  5: [ // Level 5 - Complex scenes, shortest display (0.75s)
    {
      id: 21,
      imageUrl: "https://images.unsplash.com/photo-1766324302288-1e75927a9e6d?w=800&q=80",
      displayTime: 750,
      question: "What station scene was shown?",
      answers: [
        { text: "Train speeding through station", correct: true },
        { text: "Stationary train", correct: false },
        { text: "Night time scene", correct: true },
        { text: "Daytime scene", correct: false },
        { text: "Station lights illuminated", correct: true },
        { text: "No lighting visible", correct: false },
      ]
    },
    {
      id: 22,
      imageUrl: "https://images.unsplash.com/photo-1772696572639-34c03975b58a?w=800&q=80",
      displayTime: 750,
      question: "What platform features were visible?",
      answers: [
        { text: "Train at platform at dusk", correct: true },
        { text: "Daytime bright sunlight", correct: false },
        { text: "Red/maroon coloured train", correct: true },
        { text: "Blue train", correct: false },
        { text: "Platform edge visible", correct: true },
        { text: "No platform visible", correct: false },
      ]
    },
    {
      id: 23,
      imageUrl: "https://images.unsplash.com/photo-1772696573070-c6477de0ce6e?w=800&q=80",
      displayTime: 750,
      question: "What empty station scene was shown?",
      answers: [
        { text: "Empty platform at dusk", correct: true },
        { text: "Crowded platform", correct: false },
        { text: "Railway tracks visible", correct: true },
        { text: "No tracks visible", correct: false },
        { text: "Platform lighting on", correct: true },
        { text: "Complete darkness", correct: false },
      ]
    },
    {
      id: 24,
      imageUrl: "https://images.unsplash.com/photo-1765899706337-2be7ffdafbba?w=800&q=80",
      displayTime: 750,
      question: "What night station elements were visible?",
      answers: [
        { text: "Night time platform", correct: true },
        { text: "Daytime scene", correct: false },
        { text: "Station lights illuminated", correct: true },
        { text: "No lighting", correct: false },
        { text: "Empty platform area", correct: true },
        { text: "Crowded with passengers", correct: false },
      ]
    },
    {
      id: 25,
      imageUrl: "https://images.unsplash.com/photo-1523667071851-4fda8c8a8dd5?w=800&q=80",
      displayTime: 750,
      question: "What high-speed train features were visible?",
      answers: [
        { text: "Modern bullet train", correct: true },
        { text: "Old steam locomotive", correct: false },
        { text: "Streamlined aerodynamic design", correct: true },
        { text: "Boxy traditional design", correct: false },
        { text: "Station platform visible", correct: true },
        { text: "Open countryside only", correct: false },
      ]
    },
    {
      id: 501,
      imageUrl: "https://images.unsplash.com/photo-1576745515893-3042060c8d83?w=800&q=80",
      displayTime: 750,
      question: "What train and track scene was shown?",
      answers: [
        { text: "Orange/red train on tracks", correct: true },
        { text: "Blue train", correct: false },
        { text: "Daytime railway scene", correct: true },
        { text: "Night time scene", correct: false },
        { text: "Autumn trees visible", correct: true },
        { text: "Snow covered landscape", correct: false },
      ]
    },
    {
      id: 502,
      imageUrl: "https://images.unsplash.com/photo-1713918308760-5c9a02a8ca46?w=800&q=80",
      displayTime: 750,
      question: "What modern train features were visible?",
      answers: [
        { text: "Silver and red train", correct: true },
        { text: "Black train only", correct: false },
        { text: "Train on railway tracks", correct: true },
        { text: "Train in depot", correct: false },
        { text: "Modern design styling", correct: true },
        { text: "Historic/vintage train", correct: false },
      ]
    },
    {
      id: 503,
      imageUrl: "https://images.unsplash.com/photo-1527295110-5145f6b148d0?w=800&q=80",
      displayTime: 750,
      question: "What station platform scene was shown?",
      answers: [
        { text: "Modern high-speed train", correct: true },
        { text: "Old steam train", correct: false },
        { text: "Indoor station setting", correct: true },
        { text: "Outdoor platform only", correct: false },
        { text: "White and red colours", correct: true },
        { text: "All black train", correct: false },
      ]
    },
  ],
};

const PerceptionGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [score, setScore] = useState(0);
  const [currentImage, setCurrentImage] = useState(0);
  const [showImage, setShowImage] = useState(false);
  const [showQuestion, setShowQuestion] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState(new Set());
  const [feedback, setFeedback] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const startTime = useRef(Date.now());

  // Get scenes for current level (each level has unique scenes) - with shuffled answers
  // Randomly select 5 scenes from the available pool for this level
  const gameScenes = React.useMemo(() => {
    const scenes = LEVEL_SCENES[level] || LEVEL_SCENES[1];
    // Shuffle all scenes and pick first 5
    const shuffledScenes = [...scenes].sort(() => Math.random() - 0.5).slice(0, 5);
    // Shuffle answers within each selected scene
    return shuffledScenes.map(scene => ({
      ...scene,
      answers: [...scene.answers].sort(() => Math.random() - 0.5)
    }));
  }, [level]);

  // Preload images
  useEffect(() => {
    gameScenes.forEach(scene => {
      const img = new Image();
      img.src = scene.imageUrl;
    });
  }, []);

  // Handle image display timing - use a ref to track if we're already processing
  const isProcessingRef = useRef(false);
  const retryCountRef = useRef(0);
  
  useEffect(() => {
    if (gameState !== GAME_STATES.PLAYING || currentImage >= gameScenes.length) return;
    if (showQuestion) return; // Already showing question, don't restart
    if (showImage) return; // Already showing image
    if (isProcessingRef.current) return; // Already processing
    
    isProcessingRef.current = true;
    retryCountRef.current = 0;
    let hideTimer = null;
    let loadTimeout = null;
    let isMounted = true;
    
    // First load the image
    const img = new Image();
    
    const handleImageReady = () => {
      if (!isMounted) return;
      setImageLoaded(true);
      setShowImage(true);
      
      // Display time reduces with level (but minimum 700ms)
      const displayTime = Math.max(700, gameScenes[currentImage].displayTime - level * 50);
      
      hideTimer = setTimeout(() => {
        if (!isMounted) return;
        setShowImage(false);
        setShowQuestion(true);
        isProcessingRef.current = false;
      }, displayTime);
    };
    
    img.onload = handleImageReady;
    
    // Fallback if image doesn't load within 3 seconds - show question anyway
    loadTimeout = setTimeout(() => {
      if (!isMounted) return;
      // If still not showing image or question, force proceed
      if (!showImage && !showQuestion) {
        console.warn('Image load timeout, proceeding to question');
        setImageLoaded(true);
        setShowImage(false);
        setShowQuestion(true);
        isProcessingRef.current = false;
      }
    }, 3000);
    
    img.onerror = () => {
      if (!isMounted) return;
      console.warn('Image failed to load:', gameScenes[currentImage].imageUrl);
      setShowImage(false);
      setShowQuestion(true);
      isProcessingRef.current = false;
    };
    
    img.src = gameScenes[currentImage].imageUrl;
    
    return () => {
      isMounted = false;
      isProcessingRef.current = false; // Always reset on cleanup
      if (hideTimer) clearTimeout(hideTimer);
      if (loadTimeout) clearTimeout(loadTimeout);
    };
  }, [gameState, currentImage, level, gameScenes]);

  // Fallback: auto-retry if stuck in loading state for more than 2 seconds
  useEffect(() => {
    if (gameState === GAME_STATES.PLAYING && !showImage && !showQuestion && currentImage < gameScenes.length) {
      const stuckTimer = setTimeout(() => {
        console.warn('Stuck in loading state, forcing retry');
        isProcessingRef.current = false;
        setShowQuestion(true);
      }, 2000);
      return () => clearTimeout(stuckTimer);
    }
  }, [gameState, showImage, showQuestion, currentImage, gameScenes.length]);

  const toggleAnswer = (index) => {
    const newSelected = new Set(selectedAnswers);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedAnswers(newSelected);
  };

  const submitAnswers = () => {
    const scene = gameScenes[currentImage];
    let correctCount = 0;
    let wrongCount = 0;
    
    scene.answers.forEach((answer, idx) => {
      const wasSelected = selectedAnswers.has(idx);
      if (answer.correct && wasSelected) {
        correctCount++;
      } else if (!answer.correct && wasSelected) {
        wrongCount++;
      }
    });

    const totalCorrect = scene.answers.filter(a => a.correct).length;
    const scoreForRound = Math.max(0, (correctCount - wrongCount) * 10);
    
    // Compute final tallies explicitly to avoid stale-closure on the last scene
    const isPerfect = correctCount === totalCorrect && wrongCount === 0;
    const isWrong = !isPerfect && correctCount <= wrongCount;
    const newScore = score + scoreForRound;
    const newCorrect = correct + (isPerfect ? 1 : 0);
    const newWrong = wrong + (isWrong ? 1 : 0);
    
    setScore(s => s + scoreForRound);
    
    if (isPerfect) {
      setCorrect(c => c + 1);
      setFeedback({ type: 'correct', message: 'Perfect observation!' });
    } else if (correctCount > wrongCount) {
      setFeedback({ type: 'partial', message: `${correctCount}/${totalCorrect} correct observations` });
    } else {
      setWrong(w => w + 1);
      setFeedback({ type: 'wrong', message: `Only ${correctCount}/${totalCorrect} correct` });
    }

    setTimeout(() => {
      setFeedback(null);
      setImageLoaded(false);
      setShowImage(false);
      setShowQuestion(false);
      isProcessingRef.current = false; // Reset processing flag
      if (currentImage + 1 >= gameScenes.length) {
        finishGame(newScore, newCorrect, newWrong);
      } else {
        setCurrentImage(c => c + 1);
        setSelectedAnswers(new Set());
      }
    }, 1500);
  };

  const finishGame = (finalScore = score, finalCorrect = correct, finalWrong = wrong) => {
    setGameState(GAME_STATES.FINISHED);
    const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
    const accuracy = finalCorrect + finalWrong > 0 ? (finalCorrect / (finalCorrect + finalWrong)) * 100 : 0;
    // Calculate actual max score based on correct answers per scene
    const maxPossibleScore = gameScenes.reduce((total, scene) => {
      const correctAnswersInScene = scene.answers.filter(a => a.correct).length;
      return total + (correctAnswersInScene * 10);
    }, 0);
    onComplete({ score: finalScore, maxScore: maxPossibleScore, accuracy, timeSpent });
  };

  const startGame = () => {
    setGameState(GAME_STATES.PLAYING);
    setScore(0);
    setCurrentImage(0);
    setCorrect(0);
    setWrong(0);
    setSelectedAnswers(new Set());
    setShowImage(false);
    setShowQuestion(false);
    setImageLoaded(false);
    isProcessingRef.current = false; // Reset processing flag when starting
    startTime.current = Date.now();
  };

  if (gameState === GAME_STATES.READY) {
    return <DeviceModeSelector onSelect={(mode) => { setDeviceMode(mode); setGameState(GAME_STATES.DEVICE_SELECT); }} />;
  }

  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-[#FF9F1C]/10 rounded-full flex items-center justify-center">
              <Eye className="w-8 h-8 sm:w-10 sm:h-10 text-[#FF9F1C]" />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              Speed & Distance Judgement
            </h2>
            <p className="text-sm text-[#64748B]">Level {level} - Visual Perception</p>
            <p className="text-xs text-[#FF9F1C] font-medium mt-2">
              Develops skills to improve performance on the ATAVT Perception Test
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] rounded-sm p-3 sm:p-4 mb-4 sm:mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-3 text-sm sm:text-base">How to Play:</h3>
            <ol className="space-y-2 text-xs sm:text-sm text-[#64748B]">
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">1.</span>
                A railway scene image will flash briefly (~1 second)
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">2.</span>
                Observe and memorise as much detail as possible
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">3.</span>
                After it disappears, select <strong>ALL</strong> correct observations
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">4.</span>
                Take your time answering - there's no time limit for answers
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">5.</span>
                Multiple answers may be correct - select all that apply
              </li>
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-amber-800">
              <strong>RIS-3751-TOM:</strong> This exercise develops skills assessed by ATAVT (Adaptive Tachistoscopic Traffic Test) - measuring ability to quickly perceive and recall details from brief visual presentations, essential for anticipating railway situations.
            </p>
          </div>
          
          <Button 
            onClick={startGame}
            size="lg"
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            data-testid="start-game-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Test ({gameScenes.length} scenes)
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Show image phase
  if (showImage && currentImage < gameScenes.length) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-[#64748B]">
            Scene {currentImage + 1}/{gameScenes.length}
          </Badge>
          <Badge className="bg-[#FF9F1C] animate-pulse">OBSERVE NOW</Badge>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative aspect-video bg-slate-900 rounded-sm overflow-hidden"
        >
          <img 
            src={gameScenes[currentImage].imageUrl}
            alt="Railway scene"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black/50 px-4 py-2 rounded">
              <p className="text-white text-sm font-mono">Memorise this scene!</p>
            </div>
          </div>
        </motion.div>

        <div className="text-center text-[#64748B]">
          <p className="animate-pulse">Image will disappear shortly...</p>
        </div>
      </div>
    );
  }

  // Show question phase
  if (showQuestion && currentImage < gameScenes.length) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between">
          <ScoreDisplay score={score} />
          <Badge variant="outline" className="text-[#64748B]">
            Scene {currentImage + 1}/{gameScenes.length}
          </Badge>
        </div>

        <Card className="border-slate-200">
          <CardContent className="pt-4 sm:pt-6">
            <p className="text-base sm:text-lg font-medium text-[#0B1D51] mb-4">
              {gameScenes[currentImage].question}
            </p>
            
            <div className="space-y-2 sm:space-y-3">
              {gameScenes[currentImage].answers.map((answer, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  className={`w-full text-left justify-start h-auto py-3 px-3 sm:px-4 text-sm sm:text-base ${
                    selectedAnswers.has(idx)
                      ? 'border-[#FF9F1C] bg-[#FF9F1C]/10'
                      : ''
                  }`}
                  onClick={() => toggleAnswer(idx)}
                  data-testid={`answer-${idx}`}
                >
                  <div className={`w-5 h-5 rounded border mr-3 flex items-center justify-center flex-shrink-0 ${
                    selectedAnswers.has(idx) ? 'bg-[#FF9F1C] border-[#FF9F1C]' : 'border-slate-300'
                  }`}>
                    {selectedAnswers.has(idx) && <CheckCircle className="w-4 h-4 text-white" />}
                  </div>
                  <span>{answer.text}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Button 
          onClick={submitAnswers}
          size="lg"
          className="w-full bg-[#2CB67D] hover:bg-[#2CB67D]/90"
          disabled={selectedAnswers.size === 0}
          data-testid="submit-answers-btn"
        >
          <CheckCircle className="w-5 h-5 mr-2" />
          Submit Observations
        </Button>
        
        {feedback && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-3 sm:p-4 rounded-sm text-center font-medium ${
              feedback.type === 'correct' ? 'bg-[#2CB67D] text-white' : 
              feedback.type === 'partial' ? 'bg-[#FF9F1C] text-white' :
              'bg-[#EF4444] text-white'
            }`}
          >
            {feedback.message}
          </motion.div>
        )}
      </div>
    );
  }

  // Loading state
  return (
    <div className="flex items-center justify-center min-h-[300px]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1D51] mx-auto mb-4" />
        <p className="text-[#64748B]">Loading next scene...</p>
        <Button 
          variant="outline" 
          size="sm" 
          className="mt-4"
          onClick={() => {
            isProcessingRef.current = false;
            setShowQuestion(true);
          }}
        >
          Skip to Question
        </Button>
      </div>
    </div>
  );
};

export default PerceptionGame;
